import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Tracks whether the public Changelog has new entries since the user last
 * visited /changelog. Backend already exposes `last_updated` (YYYY-MM-DD,
 * the most recent release date including completed backlog ideas) on the
 * public `/api/changelog` payload, so the frontend just compares strings.
 *
 * Persistence: `localStorage["changelog_last_visit_iso"] = "YYYY-MM-DD"`
 * stamped when the user visits the page (see `markChangelogVisited` below).
 *
 * Pattern: lightweight, no auth required (changelog is public). Failures
 * silently return `false` — a missing dot is acceptable, a wrong dot is not.
 */
const STORAGE_KEY = 'changelog_last_visit_iso';

export function useChangelogUnread() {
  const [hasUnread, setHasUnread] = useState(false);
  const [latestDate, setLatestDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${apiBase}/api/changelog`)
      .then(res => {
        if (cancelled) return;
        const last = res.data?.last_updated;
        if (!last) return;
        setLatestDate(last);
        const seen = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
        setHasUnread(!seen || seen < last);
      })
      .catch(() => { /* swallow — non vogliamo dot rosso da errori network */ });
    // Listener: quando l'utente apre il /changelog il dot deve sparire
    // istantaneamente senza ricaricare la pagina.
    const onVisited = (e) => {
      const last = e?.detail?.latestDate;
      if (last) setLatestDate(last);
      setHasUnread(false);
    };
    window.addEventListener('changelog:visited', onVisited);
    return () => {
      cancelled = true;
      window.removeEventListener('changelog:visited', onVisited);
    };
  }, []);

  return { hasUnread, latestDate };
}

/** Mark the changelog as fully read. Call when the page mounts. */
export function markChangelogVisited(latestDate) {
  if (!latestDate) return;
  try { localStorage.setItem(STORAGE_KEY, latestDate); } catch { /* private mode */ }
  // Notify other components on the same tab so the dot disappears immediately.
  try { window.dispatchEvent(new CustomEvent('changelog:visited', { detail: { latestDate } })); } catch { /* */ }
}
